import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Interrupteur } from './Interrupteur';
import { t } from '../i18n';
import { BLOC_LTR, TEXTE_LTR } from '../i18n/sens';
import { colors, formes, shadow, spacing, typography } from '../theme/tokens';

export interface CarteModificationBaseProps {
  // Nature de la modification, déjà traduite (« Impact », « Accroche »).
  tag: string;
  // Où se trouve la modification, déjà traduit ou tiré du document ; null si on ne sait pas le dire.
  lieu: string | null;
  // Vrai quand `lieu` est du texte du document (rôle, entreprise) : il reste de gauche à droite.
  lieuDuDocument?: boolean;
  reason: string;
  before: string;
  after: string;
  // Libellé lu par l'assistant vocal pour l'interrupteur.
  nomInterrupteur: string;
  retenue: boolean;
  onToggle: (retenue: boolean) => void;
  // Figé pendant l'enregistrement : on ne change plus ce qui est en train d'être appliqué.
  disabled?: boolean;
  testID?: string;
}

/**
 * Une modification proposée, quel que soit le document : sa nature, l'interrupteur pour la garder,
 * l'explication, puis le texte d'avant et le texte d'après. Partagée par l'optimisation du CV et
 * l'amélioration de la lettre ; chacune fournit ses libellés.
 *
 * Les deux textes sont ceux du document : ils restent de gauche à droite, quelle que soit la langue
 * de l'interface. L'explication, elle, suit la langue de l'interface. Aucun gain en points : la
 * modification n'est pas notée, on n'affiche donc aucun chiffre.
 */
export function CarteModificationBase({
  tag,
  lieu,
  lieuDuDocument = false,
  reason,
  before,
  after,
  nomInterrupteur,
  retenue,
  onToggle,
  disabled = false,
  testID = 'modif',
}: CarteModificationBaseProps) {
  return (
    <View testID={testID} style={styles.carte}>
      <View style={styles.entete}>
        <View style={styles.identite}>
          <View style={styles.pastille}>
            <Text testID={`${testID}-tag`} style={styles.pastilleTexte}>
              {tag}
            </Text>
          </View>
          {lieu ? (
            <Text testID={`${testID}-lieu`} style={[styles.lieu, lieuDuDocument && TEXTE_LTR]} numberOfLines={2}>
              {lieu}
            </Text>
          ) : null}
        </View>
        <Interrupteur
          testID={`${testID}-interrupteur`}
          value={retenue}
          onValueChange={onToggle}
          disabled={disabled}
          label={nomInterrupteur}
        />
      </View>

      {reason ? (
        <Text testID={`${testID}-raison`} style={styles.raison}>
          {reason}
        </Text>
      ) : null}

      <View style={[styles.textes, BLOC_LTR]}>
        <View style={[styles.bloc, styles.avant]}>
          <Text style={styles.etiquette}>{t('cvImprove.modif.avant')}</Text>
          <Text testID={`${testID}-avant`} style={[styles.texteAvant, TEXTE_LTR]}>
            {before}
          </Text>
        </View>
        <View style={[styles.bloc, retenue ? styles.apresRetenue : styles.apresRefusee]}>
          <Text style={styles.etiquette}>{t('cvImprove.modif.apres')}</Text>
          <Text testID={`${testID}-apres`} style={[styles.texteApres, !retenue && styles.texteRefuse, TEXTE_LTR]}>
            {after}
          </Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  carte: {
    gap: spacing.md,
    padding: spacing.lg,
    borderRadius: formes.rayon.lg,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  entete: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  identite: { flex: 1, minWidth: 0, gap: spacing.xs, alignItems: 'flex-start' },
  pastille: {
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: spacing.xs,
    borderRadius: formes.rayon.sm,
    backgroundColor: colors.blueSoft,
  },
  pastilleTexte: { fontSize: typography.caption.size + 1, fontWeight: '800', color: colors.electricDeep },
  lieu: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, fontWeight: '700', color: colors.night },
  raison: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  textes: { gap: spacing.sm },
  bloc: { gap: spacing.xs, padding: spacing.md, borderRadius: formes.rayon.sm },
  avant: { backgroundColor: colors.grey50 },
  apresRetenue: { backgroundColor: colors.successSoft },
  apresRefusee: { backgroundColor: colors.grey100 },
  etiquette: {
    fontSize: typography.caption.size,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing,
    textTransform: 'uppercase',
    color: colors.grey500,
  },
  texteAvant: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  texteApres: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, fontWeight: '600', color: colors.night },
  texteRefuse: { color: colors.grey500, fontWeight: '400' },
});
